import { useEffect, useRef } from "react";
import LoadingScreen from "../components/loadingScreen/LoadingScreen";
import { storeCodeVerifier, getStoredCodeVerifier, clearCodeVerifier, createPkceChallengePair } from "../utils/pkce-utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "../api/axiosConfig";
import { Capacitor } from "@capacitor/core";
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { setItem, clear, getItem } from "../utils/storageHelper";
import { openNativeAuthSession, getGoogleIosOAuthRedirectUri } from "../utils/nativeAuthBrowser";
import * as Sentry from "@sentry/capacitor";

const ANDROID_CALLBACK_GRACE_MS = 3000;
const CALENDAR_AUTH_STATE_KEY = 'calendar_auth_state';
const CALENDAR_AUTH_REDIRECT_URI_KEY = 'calendar_auth_redirect_uri';

const EXPECTED_AUTH_ERROR_CODES = new Set([
    'STATE_MISMATCH_ERROR',
    'OAUTH_ERROR',
    'MISSING_CODE_VERIFIER',
    'MISSING_VERIFIER',
]);

function getCalendarRedirectUri(providerInfo) {
    if (Capacitor.isNativePlatform()) {
        if (providerInfo === "microsoft") {
            if (Capacitor.getPlatform() === 'ios') {
                return import.meta.env.VITE_MICROSOFT_CALENDAR_IOS_REDIRECT_URI;
            }
            return import.meta.env.VITE_MICROSOFT_CALENDAR_ANDROID_REDIRECT_URI;
        }
        if (Capacitor.getPlatform() === 'ios') {
            return getGoogleIosOAuthRedirectUri(
                import.meta.env.VITE_IOS_MOBILE_CLIENT_ID,
                import.meta.env.VITE_IOS_REDIRECT
            );
        }
        return import.meta.env.VITE_GOOGLE_CALENDAR_ANDROID_REDIRECT_URI;
    }
    return import.meta.env.VITE_CALENDAR_REDIRECT_URI;
}

async function clearCalendarAuthArtifacts() {
    clearCodeVerifier();
    await Promise.allSettled([
        Preferences.remove({ key: CALENDAR_AUTH_STATE_KEY }),
        Preferences.remove({ key: CALENDAR_AUTH_REDIRECT_URI_KEY }),
    ]);
}

function shouldCaptureSentry(errorCode, originalError) {
    if (!originalError) {
        return false;
    }
    if (EXPECTED_AUTH_ERROR_CODES.has(errorCode)) {
        return false;
    }
    if (originalError.code === 'CANCELED') {
        return false;
    }
    const status = originalError.response?.status;
    if (status === 401 || status === 403) {
        return false;
    }
    return true;
}

export default function CalendarLogin() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const providerInfo = searchParams.get("type") || "Unknown";
    const appUrlOpenListenerRef = useRef(null);
    const appUrlOpenListenerOperationRef = useRef(Promise.resolve());
    const authInProgress = useRef(false);
    const authCallbackReceived = useRef(false);
    const callbackProcessingRef = useRef(false);
    const browserCloseGraceTimerRef = useRef(null);
    const calendarLoginActiveRef = useRef(true);

    const clearBrowserCloseGraceTimer = () => {
        if (browserCloseGraceTimerRef.current) {
            clearTimeout(browserCloseGraceTimerRef.current);
            browserCloseGraceTimerRef.current = null;
        }
    };

    const handleError = async (errorCode, errorMessage, originalError = null) => {
        console.error(`Error [${errorCode}]:`, errorMessage, originalError);

        if (shouldCaptureSentry(errorCode, originalError)) {
            Sentry.captureException(originalError, {
                tags: {
                    error_type: 'calendar_login_failure',
                    error_code: errorCode,
                    is_native: Capacitor.isNativePlatform(),
                },
                level: 'error',
                extra: { error_message: errorMessage }
            });
        }

        try {
            sessionStorage.clear();
            await clear();
        } catch (clearError) {
            console.error("Error clearing storage:", clearError);
        }

        navigate(`/Error?errorCode=${encodeURIComponent(errorCode)}&errorMessage=${encodeURIComponent(errorMessage)}`);
    };

    const isCalendarCallbackUrl = async (urlString) => {
        const { value: storedRedirectUri } = await Preferences.get({ key: CALENDAR_AUTH_REDIRECT_URI_KEY });
        const redirectUri = storedRedirectUri || getCalendarRedirectUri(providerInfo);
        return Boolean(redirectUri && urlString.startsWith(redirectUri));
    };

    useEffect(() => {
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        let backButtonHandle;
        let cancelled = false;

        App.addListener('backButton', async () => {
            if (authCallbackReceived.current) {
                return;
            }
            clearBrowserCloseGraceTimer();
            authInProgress.current = false;
            calendarLoginActiveRef.current = false;
            await clearCalendarAuthArtifacts();
            if (cancelled) {
                return;
            }
            navigate('/Calendar', { replace: true });
        }).then((handle) => {
            if (cancelled) {
                handle.remove();
                return;
            }
            backButtonHandle = handle;
        }).catch((error) => {
            if (!cancelled) {
                console.error('[NATIVE CALENDAR LOGIN] Failed to set up backButton listener:', error);
            }
        });

        return () => {
            cancelled = true;
            backButtonHandle?.remove();
        };
    }, [navigate]);

    useEffect(() => {
        let cancelled = false;
        calendarLoginActiveRef.current = true;
        const enqueueListenerOperation = (operation) => {
            const result = appUrlOpenListenerOperationRef.current.then(operation);
            appUrlOpenListenerOperationRef.current = result.catch(() => {});
            return result;
        };

        console.log("CalendarLogin component mounted");

        // Set up app URL open listener for Android (iOS uses ASWebAuthenticationSession)
        if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
            console.log("Setting up Android URL listeners for calendar");

            enqueueListenerOperation(async () => {
                const previousHandle = appUrlOpenListenerRef.current;
                appUrlOpenListenerRef.current = null;
                await previousHandle?.remove();
                if (cancelled) {
                    return;
                }
                const handle = await App.addListener('appUrlOpen', async (data) => {
                    if (cancelled) {
                        return;
                    }
                    try {
                        console.log("App URL Open Event:", data?.url || "no URL");

                        if (data && data.url) {
                            const urlString = data.url;

                            try {
                                new URL(urlString);
                            } catch (e) {
                                console.error("Failed to parse URL:", e);
                                handleError("URL_PARSE_ERROR", "Failed to parse callback URL", e);
                                return;
                            }

                            const isCalendarCallback = await isCalendarCallbackUrl(urlString);

                            console.log("Is Calendar callback:", isCalendarCallback);

                            if (isCalendarCallback) {
                                console.log("Processing calendar callback");
                                clearBrowserCloseGraceTimer();
                                authCallbackReceived.current = true;
                                await nativeCalendarCallback(urlString);
                            } else {
                                console.log("Not a calendar callback, navigating to Calendar");
                                navigate('/Calendar');
                            }
                        } else {
                            console.log("No URL data in app open event - navigating to Calendar");
                            navigate('/Calendar');
                        }
                    } catch (error) {
                        handleError("APP_URL_LISTENER_ERROR", "Error in app URL listener", error);
                    }
                });
                if (cancelled) {
                    await handle.remove();
                    return;
                }
                appUrlOpenListenerRef.current = handle;
            }).catch((error) => {
                if (!cancelled) {
                    handleError("LISTENER_SETUP_ERROR", "Failed to set up URL listeners", error);
                }
            });
        }

        const handleInitialLoad = async () => {
            try {
                let accessToken = localStorage.getItem("calendar_access_token");

                if (!Capacitor.isNativePlatform()) {
                    try {
                        const urlParams = new URLSearchParams(window.location.search);
                        const code = urlParams.get("code");
                        const storedVerifier = await getStoredCodeVerifier();
                        if (cancelled) {
                            return;
                        }

                        if (code && storedVerifier) {
                            try {
                                await processAuthCode(code, storedVerifier);
                                return;
                            } catch (error) {
                                console.error("Auth callback error:", error);
                                handleError("AUTH_CALLBACK_ERROR", "Failed to process authentication callback", error);
                                return;
                            }
                        } else if (code && !storedVerifier) {
                            handleError("MISSING_VERIFIER", "Code verifier not found. Authentication flow cannot complete.");
                            return;
                        }
                    } catch (error) {
                        handleError("WEB_CALLBACK_ERROR", "Error processing web callback", error);
                        return;
                    }
                }

                if (accessToken === null || accessToken === '') {
                    try {
                        await initiateLogin(providerInfo);
                    } catch (error) {
                        handleError("LOGIN_INITIATION_ERROR", "Failed to initiate login process", error);
                    }
                } else if (!cancelled) {
                    navigate("/Calendar");
                }
            } catch (error) {
                if (!cancelled) {
                    handleError("INITIAL_LOAD_ERROR", "Error during initial load", error);
                }
            }
        };

        handleInitialLoad();

        return () => {
            cancelled = true;
            calendarLoginActiveRef.current = false;
            clearBrowserCloseGraceTimer();
            enqueueListenerOperation(async () => {
                const handle = appUrlOpenListenerRef.current;
                appUrlOpenListenerRef.current = null;
                await handle?.remove();
            });
        };
    }, [navigate]);

    const processAuthCode = async (code, verifier) => {
        try {
            let resolvedProvider;
            try {
                if (Capacitor.isNativePlatform()) {
                    const result = await Preferences.get({ key: 'calendarProvider' });
                    resolvedProvider = result.value;
                } else {
                    resolvedProvider = localStorage.getItem("calendarProvider");
                }
            } catch (error) {
                handleError("PROVIDER_RETRIEVAL_ERROR", "Failed to retrieve provider information", error);
                return;
            }

            if (resolvedProvider && resolvedProvider.startsWith('"') && resolvedProvider.endsWith('"')) {
                resolvedProvider = resolvedProvider.slice(1, -1);
            }

            console.log("Processing auth code for provider:", resolvedProvider);

            const endpoint = resolvedProvider === "microsoft"
                ? `${import.meta.env.VITE_PUBLIC_DOMAIN}Calendar/microsoftAuth`
                : `${import.meta.env.VITE_PUBLIC_DOMAIN}Calendar/googleAuth`;

            let clientType = 0;
            if (Capacitor.isNativePlatform()) {
                const platform = Capacitor.getPlatform();
                clientType = platform === 'ios' ? 2 : 1;
            }

            const redirectUrl = getCalendarRedirectUri(resolvedProvider);

            let response;
            try {
                response = await axios.post(endpoint, {
                    code: code,
                    codeVerifier: verifier,
                    redirectUrl: redirectUrl,
                    clientType: clientType
                }, {
                    headers: {
                        "Content-Type": "application/json",
                    }
                });
            } catch (error) {
                if (error.response) {
                    const errorData = error.response.data;
                    console.error("Token exchange error response:", errorData);
                    handleError("TOKEN_EXCHANGE_ERROR", `Failed to exchange code for tokens: ${errorData}`, error);
                } else {
                    handleError("TOKEN_EXCHANGE_NETWORK_ERROR", "Network error during token exchange", error);
                }
                return;
            }

            console.log("Token exchange successful, processing response");
            const tokens = response.data;

            console.log("Auth successful, setting calendar access token");

            try {
                await setItem("calendar_access_token", tokens.accessToken);
            } catch (error) {
                handleError("TOKEN_STORAGE_ERROR", "Failed to store access token", error);
                return;
            }

            console.log("Setting up calendar watch");
            try {
                await axios.post(
                    `${import.meta.env.VITE_PUBLIC_DOMAIN}Calendar/setUpCalendarWatch`,
                    {
                        AccessToken: tokens.accessToken,
                        Type: resolvedProvider
                    },
                    {
                        headers: {
                            "Content-Type": "application/json",
                        },
                    }
                );

                console.log("Calendar watch setup successful");
            } catch (watchError) {
                console.error("Calendar watch setup failed:",
                    watchError.response?.data || watchError.message);
            }

            console.log("Calendar setup complete, clearing auth artifacts");
            try {
                await clearCalendarAuthArtifacts();
            } catch (error) {
                console.error("Failed to clear auth artifacts:", error);
            }

            if (!Capacitor.isNativePlatform()) {
                try {
                    window.history.replaceState({}, document.title, window.location.pathname);
                } catch (error) {
                    console.error("Failed to replace history state:", error);
                }
            }

            const calendarProvider = await getItem("calendarProvider");
            if (calendarProvider) {
                Sentry.setTag("calendar_provider", calendarProvider.replace(/"/g, ''));
            }

            navigate("/Calendar");
        } catch (error) {
            console.error("Error during auth code processing:", error);
            handleError("AUTH_CODE_PROCESSING_ERROR", "Error during authentication code processing", error);
        }
    };

    const initiateLogin = async (providerInfo) => {
        if (!calendarLoginActiveRef.current) {
            return;
        }

        console.log("Initiating calendar login for provider:", providerInfo);
        try {
            const accessToken = await getItem("calendar_access_token");
            if (accessToken) {
                console.log("Found existing access token");
                navigate("/Calendar");
                return;
            }

            console.log("Generating PKCE challenge");
            let verifier, challenge;
            try {
                const pkceResult = await createPkceChallengePair();
                verifier = pkceResult.verifier;
                challenge = pkceResult.challenge;
                await storeCodeVerifier(verifier);
            } catch (error) {
                handleError("PKCE_GENERATION_ERROR", "Failed to generate PKCE challenge", error);
                return;
            }

            let clientId, redirectUri, scope, authorizationEndpoint, authorizationUrl;

            if (Capacitor.isNativePlatform()) {
                if (providerInfo === "microsoft") {
                    clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID;
                    redirectUri = getCalendarRedirectUri("microsoft");
                    scope = "openid profile email offline_access Calendars.ReadWrite";
                    authorizationEndpoint = "https://login.microsoftonline.com/common/oauth2/v2.0/authorize";
                    console.log("Using Microsoft native auth with redirect URI:", redirectUri);
                } else {
                    if (Capacitor.getPlatform() === 'ios') {
                        clientId = import.meta.env.VITE_IOS_MOBILE_CLIENT_ID;
                    } else {
                        clientId = import.meta.env.VITE_ANDROID_MOBILE_CLIENT_ID;
                        console.log("Using Android client ID:", clientId);
                    }
                    redirectUri = getCalendarRedirectUri(providerInfo);
                    scope = "openid profile email https://www.googleapis.com/auth/calendar";
                    authorizationEndpoint = "https://accounts.google.com/o/oauth2/v2/auth";
                    console.log("Using Google native redirect URI:", redirectUri);
                }
            } else if (providerInfo === "microsoft") {
                clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID;
                redirectUri = getCalendarRedirectUri("microsoft");
                scope = "openid profile email offline_access Calendars.ReadWrite";
                authorizationEndpoint = "https://login.microsoftonline.com/common/oauth2/v2.0/authorize";
            } else {
                clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
                redirectUri = getCalendarRedirectUri(providerInfo);
                scope = "openid profile email https://www.googleapis.com/auth/calendar";
                authorizationEndpoint = "https://accounts.google.com/o/oauth2/v2/auth";
            }

            if (!clientId) {
                handleError("MISSING_CLIENT_ID", `Client ID not found for provider: ${providerInfo}`);
                return;
            }
            if (!redirectUri) {
                handleError("MISSING_REDIRECT_URI", "Redirect URI not configured");
                return;
            }

            let state;
            try {
                state = (crypto.randomUUID?.() ??
                    ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
                        (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
                    ));
                console.log("Generated state parameter for security");
            } catch (error) {
                handleError("STATE_GENERATION_ERROR", "Failed to generate state parameter", error);
                return;
            }

            if (providerInfo === "microsoft") {
                authorizationUrl = `${authorizationEndpoint}?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&code_challenge=${encodeURIComponent(challenge)}&code_challenge_method=S256&response_mode=query&prompt=select_account&state=${state}&`;
            } else {
                authorizationUrl = `${authorizationEndpoint}?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&code_challenge=${encodeURIComponent(challenge)}&code_challenge_method=S256&access_type=offline&prompt=consent&state=${state}`;
            }

            console.log("Authorization URL created:", authorizationUrl);
            console.log("Platform is native:", Capacitor.isNativePlatform());

            if (Capacitor.isNativePlatform()) {
                await nativeCalendarLogin(authorizationUrl, state, redirectUri);
            } else {
                try {
                    window.location.href = authorizationUrl;
                } catch (error) {
                    handleError("REDIRECT_ERROR", "Failed to redirect to authorization URL", error);
                }
            }
        } catch (error) {
            console.error("Login initiation error:", error);
            handleError("LOGIN_INITIATION_ERROR", "Failed to initiate login", error);
        }
    };

    const nativeCalendarLogin = async (authUrl, state, redirectUri) => {
        console.log("Starting native calendar login");
        console.log("Authorization URL:", authUrl);

        try {
            try {
                await Preferences.set({ key: CALENDAR_AUTH_STATE_KEY, value: state });
                await Preferences.set({ key: CALENDAR_AUTH_REDIRECT_URI_KEY, value: redirectUri });
            } catch (error) {
                handleError("STATE_STORAGE_ERROR", "Failed to store authentication state", error);
                return;
            }

            authCallbackReceived.current = false;
            authInProgress.current = true;

            const callbackUrl = await openNativeAuthSession(authUrl, redirectUri, {
                onBrowserFinished: Capacitor.getPlatform() === 'android'
                    ? () => {
                        if (!authInProgress.current || authCallbackReceived.current) {
                            return;
                        }
                        console.log('[NATIVE CALENDAR LOGIN] Browser closed during authentication');
                        clearBrowserCloseGraceTimer();
                        browserCloseGraceTimerRef.current = setTimeout(async () => {
                            browserCloseGraceTimerRef.current = null;
                            if (authCallbackReceived.current) {
                                return;
                            }
                            authInProgress.current = false;
                            await clearCalendarAuthArtifacts();
                            navigate('/Calendar');
                        }, ANDROID_CALLBACK_GRACE_MS);
                    }
                    : undefined,
            });

            if (callbackUrl) {
                clearBrowserCloseGraceTimer();
                authCallbackReceived.current = true;
                await nativeCalendarCallback(callbackUrl);
            }
        } catch (error) {
            authInProgress.current = false;

            if (error?.code === 'CANCELED') {
                console.log('[NATIVE CALENDAR LOGIN] User canceled authentication');
                clearBrowserCloseGraceTimer();
                await clearCalendarAuthArtifacts();
                navigate('/Calendar');
                return;
            }

            console.error("Native calendar login error:", error);
            handleError("NATIVE_LOGIN_ERROR", `Calendar login error: ${error.message}`, error);
        }
    };

    const nativeCalendarCallback = async (urlString) => {
        if (callbackProcessingRef.current) {
            return;
        }
        callbackProcessingRef.current = true;
        authCallbackReceived.current = true;
        clearBrowserCloseGraceTimer();

        console.log("Processing native calendar callback");
        try {
            if (urlString.length > 0) {
                let url;
                try {
                    url = new URL(urlString);
                } catch (error) {
                    handleError("CALLBACK_URL_PARSE_ERROR", "Failed to parse callback URL", error);
                    return;
                }

                const returnedCode = url.searchParams.get('code');
                const returnedState = url.searchParams.get('state');
                const returnedError = url.searchParams.get('error');

                console.log("Found code:", returnedCode ? "YES" : "NO");
                console.log("Found state:", returnedState ? "YES" : "NO");
                console.log("Found error:", returnedError || "NO");

                if (returnedError) {
                    const errorDescription = url.searchParams.get('error_description') || returnedError;
                    handleError("OAUTH_ERROR", `Authentication failed: ${errorDescription}`);
                    return;
                }

                let storedState;
                try {
                    const result = await Preferences.get({ key: CALENDAR_AUTH_STATE_KEY });
                    storedState = result.value;
                } catch (error) {
                    handleError("STATE_RETRIEVAL_ERROR", "Failed to retrieve stored authentication state", error);
                    return;
                }

                console.log("Stored state matches:", returnedState === storedState);

                if (returnedState !== storedState) {
                    handleError("STATE_MISMATCH_ERROR", "Authentication state mismatch - possible security issue");
                    return;
                }

                if (returnedCode && returnedState) {
                    const storedVerifier = await getStoredCodeVerifier();
                    console.log("Stored verifier exists:", storedVerifier ? "YES" : "NO");

                    if (!storedVerifier) {
                        handleError("MISSING_CODE_VERIFIER", "Code verifier not found. Authentication flow cannot complete.");
                        return;
                    }

                    await processAuthCode(returnedCode, storedVerifier);

                    if (Capacitor.getPlatform() !== 'ios') {
                        console.log("Processing complete, closing browser");
                        try {
                            await Browser.close();
                            console.log("Browser closed successfully");
                        } catch (e) {
                            console.log("Browser may already be closed:", e.message);
                        }
                    }
                } else {
                    handleError("INCOMPLETE_CALLBACK",
                        `Incomplete authentication callback - Missing code: ${!returnedCode}, Missing state: ${!returnedState}`);
                }
            } else {
                handleError("EMPTY_CALLBACK_URL", "Callback URL is empty");
            }
        } catch (error) {
            handleError("CALLBACK_PROCESSING_ERROR", `Error processing authentication callback: ${error.message}`, error);
        } finally {
            clearBrowserCloseGraceTimer();
            authInProgress.current = false;
        }
    };

    return <LoadingScreen />;
}
